import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Icon, OptionsMenu, Typography } from '@scale/scaleui';
import { SquadType } from '@scale/shared/types/expertManagement';
import { useCallback, useMemo, useState } from 'react';
import { AssociatedChannelsModal } from 'react-scale/components/ExpertManagement/ExpertManagementPage/AssociatedChannelsModal';
import { EditManagerInfoModal } from 'react-scale/components/ExpertManagement/ExpertManagementPage/EditManagerInfoModal';
import { ToolsLink } from 'react-scale/components/ExpertManagement/ExpertManagementPage/ToolsLink';
import { SQUAD_TYPE_TO_NAME_MAP } from 'react-scale/components/ExpertManagement/constants';
import {
  hasModifyPermissionsSelector,
  useClientStore,
} from 'react-scale/components/ExpertManagement/stores/client.store';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import { shallow } from 'zustand/shallow';
import { MoveToNewManagerModal } from './MoveToNewManagerModal';
import { promoteToTopLevelManager } from './api';
import { UserInfo } from './types';
import { Flex, Link, Icon as RadixIcon, Text } from '@scale/scaleui-radix';
import { faExternalLinkAlt } from '@fortawesome/pro-regular-svg-icons';

interface UserInfoCardProps {
  userInfo: UserInfo;
  squadType?: SquadType;
}

function maybeReload() {
  if (location) {
    location.reload();
  }
}

const ExternalLink: React.FC<{
  url: string;
  label: string;
}> = ({ url, label }) => {
  return (
    <Link href={url} target="_blank" rel="noopener noreferrer" size="1">
      <Flex gap="1" align="center">
        <Text weight="medium" size="2">
          {label}
        </Text>
        <RadixIcon icon={faExternalLinkAlt} label="link" size="1" />
      </Flex>
    </Link>
  );
};

export const UserInfoCard: React.FC<UserInfoCardProps> = (props: UserInfoCardProps) => {
  const {
    id,
    name,
    role,
    manager,
    expertSlackId,
    teamLeadPodType,
    expertSlackChannels,
    discourseUsername,
    discourseCategory,
  } = props.userInfo;
  const canModify = useClientStore(hasModifyPermissionsSelector, shallow);

  const [moveManagersModalOpen, setMoveManagersModalOpen] = useState<boolean>(false);
  const [editManagerInfoModalOpen, setEditManagerInfoModalOpen] = useState<boolean>(false);
  const [associatedChannelsModalOpen, setAssociatedChannelsModalOpen] = useState<boolean>(false);

  const moveToTopLevel = useCallback(async () => {
    const result = await promoteToTopLevelManager(id);
    if (result.success) {
      maybeReload();
    } else {
      defaultApiFetchErrorHandler({ name: 'error', message: result.error, logError: () => null });
    }
  }, [id]);

  const actions = useMemo(() => {
    const defaultActions = [
      {
        label: 'Show Associated Categories',
        onClick: () => setAssociatedChannelsModalOpen(true),
      },
    ];
    if (role === 'Manager') {
      defaultActions.push(
        { label: 'Edit Reviewer Info', onClick: () => setEditManagerInfoModalOpen(true) },
        { label: 'Change Leader', onClick: () => setMoveManagersModalOpen(true) },
        { label: 'Move to Top Level', onClick: moveToTopLevel },
      );
    } else {
      defaultActions.push({
        label: 'Change Leader',
        onClick: () => setMoveManagersModalOpen(true),
      });
    }
    return defaultActions;
  }, [role, moveToTopLevel]);

  const renamedRole =
    role === 'Manager' ? (!manager ? 'Team Lead Coordinator' : 'Team Lead') : role || 'None';

  const renderField = (label: string, value: string | JSX.Element, key?: string) => (
    <div className="flex justify-between" key={key}>
      <Typography.Subtitle3 className="my-auto">{label}</Typography.Subtitle3>
      <Typography.Subtitle3 className="my-auto">{value}</Typography.Subtitle3>
    </div>
  );

  const { isRemoAdmin } = useRemoAdminCtx();
  const isLocalEnvironment = () =>
    typeof window !== 'undefined' && window.location.hostname === 'localhost';

  return (
    <div className="flex flex-col gap-2 bg-neutral-0 p-4 border border-neutral-200 rounded-md w-[400px]">
      <MoveToNewManagerModal
        open={moveManagersModalOpen}
        onClose={() => setMoveManagersModalOpen(false)}
        expertIds={[id]}
      />
      <EditManagerInfoModal
        open={editManagerInfoModalOpen}
        onClose={() => setEditManagerInfoModalOpen(false)}
        userInfo={props.userInfo}
      />
      <AssociatedChannelsModal
        open={associatedChannelsModalOpen}
        onClose={() => setAssociatedChannelsModalOpen(false)}
        userInfo={props.userInfo}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon hasBackground className="bg-neutral-100" icon={['far', 'user']} />
          <Typography.Subtitle2>Basic Information</Typography.Subtitle2>
        </div>
        {canModify && (
          <div className="flex">
            <OptionsMenu direction="bottom-left" menuItems={actions} />{' '}
            <ToolsLink to={`/lookup/${id}`} className="flex items-center">
              <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-1" />
            </ToolsLink>
          </div>
        )}
      </div>
      {discourseUsername
        ? renderField(
            'Username',
            <ExternalLink
              url={`https://community.outlier.ai/u/${discourseUsername}/summary`}
              label={discourseUsername}
            />,
          )
        : renderField('Name', name)}
      {renderField('Level', renamedRole)}
      {renderField('Id', id)}
      {renderField('Pod Type', teamLeadPodType?.name ?? 'N/A')}
      {props.squadType &&
        renderField('Squad Type', SQUAD_TYPE_TO_NAME_MAP[props.squadType] ?? 'N/A')}
      {expertSlackId && (
        <div className="flex flex-col gap-2">
          {renderField(
            'Expert Slack',
            <a
              href={`https://expert--community.slack.com/team/${expertSlackId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center ml-1"
            >
              Profile <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-1" />
            </a>,
          )}
          {expertSlackChannels.length > 0 &&
            expertSlackChannels.map((channel, idx) =>
              renderField(
                'Expert Slack Channel',
                <a
                  href={`https://expert--community.slack.com/channels/${channel.channel_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center ml-1 my-auto"
                  key={channel.channel_id}
                >
                  {`#${channel?.channel} `}
                  <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-1" />
                </a>,
                'expert-slack-channel-' + idx,
              ),
            )}
        </div>
      )}
      {!isRemoAdmin &&
        renderField(
          'Expert Dashboard',
          <ExternalLink
            url={
              isLocalEnvironment()
                ? `http://localhost:3002/en/expert?spoof=${id}`
                : `https://app.outlier.ai/en/expert?spoof=${id}`
            }
            label="Spoof"
          />,
        )}
      {discourseCategory &&
        renderField(
          'Outlier Community Category',
          <ExternalLink
            url={`https://community.outlier.ai/c/${discourseCategory.name}/${discourseCategory.discourseId}`}
            label={discourseCategory.name}
          />,
        )}
      {discourseCategory &&
        discourseCategory.chatDiscoursePathSegment &&
        renderField(
          'Outlier Community Channel',
          <ExternalLink
            url={`https://community.outlier.ai/chat/c/${discourseCategory.chatDiscoursePathSegment}`}
            label="Channel"
          />,
        )}
    </div>
  );
};
